import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, effect, ElementRef, EventEmitter, Inject, Injector, input, OnInit, Output, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { GoogleMap, GoogleMapsModule, MapAdvancedMarker, MapInfoWindow } from '@angular/google-maps';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { longFormatters } from 'date-fns';
import { type } from 'jquery';
import { Subscription } from 'rxjs';
import { EntitiesFacade, PROVIDERS_STATUS_BACKGROUND_COLORS, PROVIDERS_STATUS_COLORS } from 'src/app/abstraction/entities.facade';
import { MapsFacade } from 'src/app/abstraction/maps.facade';
import { AuthenticationService } from 'src/app/core/authentication.service';
import { PATIENT_STATUS } from 'src/app/core/enums/patientStatus';
import { PROVIDER_STATUS, PROVIDER_STATUS_TAGS } from 'src/app/core/enums/providerStatus';
import { ROUTES } from 'src/app/core/enums/routes';
import { TypeMapMarker } from 'src/app/core/enums/typeMapMarker';
import { AlertService } from 'src/app/core/services/alert.service';
import { GeneralService } from 'src/app/core/services/general.service';
import { ProvidersQPS } from 'src/app/core/services/providers.service';
import { MaterialModule } from 'src/app/material/material.module';
import { ProvidersManagementComponent } from 'src/app/modules/data-management/pages/providers-management/providers-management/providers-management.component';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import { MarkerGoogleMaps } from 'src/app/shared/models/maps/markerGoogleMaps';
import { Specialty } from 'src/app/shared/models/specialty';
import { PipesModule } from 'src/app/shared/pipes/pipes.module';
import { MapsService } from 'src/app/shared/services/maps.service';

export interface locateMarker {
    marker: MarkerGoogleMaps,
    timeStamp: Date,
    clusterMember?: number
}

@Component({
    selector: 'app-map',
    standalone: true,
    imports: [GoogleMapsModule, MaterialModule, CommonModule, PipesModule],
    templateUrl: './map.component.html',
    styleUrl: './map.component.scss',
})

export class MapComponent implements OnInit {

    @ViewChild(GoogleMap, { static: false }) map: GoogleMap;
    @ViewChild(MapInfoWindow, { static: false }) info: MapInfoWindow;
    @ViewChild('templatePatient', { static: true }) templatePatient: TemplateRef<any>;
    @ViewChild('templateProvider', { static: true }) templateProvider: TemplateRef<any>;
    @ViewChild('templateCase', { static: true }) templateCase: TemplateRef<any>;
    @ViewChild('templateCluster', { static: true }) templateCluster: TemplateRef<any>;
    @ViewChildren('markerElem') markerElements!: QueryList<MapAdvancedMarker>;
    @ViewChild('clusterProvidersTemplate', { static: true }) clusterProvidersTemplate!: TemplateRef<any>;
    @ViewChild('providerDSPHost', { static: true }) providerDSPHost!: TemplateRef<any>;


    title = input<string>();
    specialtyName = input<string>();
    professionalTypeName = input<string>();
    specialtyId = input<number>();
    showProviders = input<boolean>();
    markerSelected = input<locateMarker>();
    width = input<number>();
    height = input<number>();
    specialtySelected = input<Specialty>();

    @Output() actualCenter = new EventEmitter<MarkerGoogleMaps>(null);
    @Output() providerShowInfo = new EventEmitter<{marker:MarkerGoogleMaps, patientsFocus?:boolean}>(null);

    private _localCenter: MarkerGoogleMaps;
    localMarkers: MarkerGoogleMaps[];
    allClusterMembers: MarkerGoogleMaps[] = [];
    selectedClusterMembers: MarkerGoogleMaps[] = [];
    markerInClusterSelected: MarkerGoogleMaps = null;
    markersSignal: MarkerGoogleMaps[];
    centerFlag: boolean = false;
    showingProviders = true;
    providerStatusTags: Record<PROVIDER_STATUS | PATIENT_STATUS, string> = {
        [PROVIDER_STATUS.ACTIVE]: PROVIDER_STATUS_TAGS.ACTIVE,
        [PROVIDER_STATUS.INACTIVE]: PROVIDER_STATUS_TAGS.INACTIVE,
        [PROVIDER_STATUS.POSSIBLE]: PROVIDER_STATUS_TAGS.POSSIBLE,
        [PROVIDER_STATUS.CANCELLED]: PROVIDER_STATUS_TAGS.CANCELLED,
    };
    patientStatusTags = {
        [PATIENT_STATUS.ACTIVE]: 'ACTIVO',
        [PATIENT_STATUS.INACTIVE]: 'INACTIVO'
    }
    providerTypes: TypeMapMarker[] = [
        TypeMapMarker.PROVIDER_ACTIVE_ADDRESS,
        TypeMapMarker.PROVIDER_ACTIVE_ZONE,
        TypeMapMarker.PROVIDER_INACTIVE_ADDRESS,
        TypeMapMarker.PROVIDER_INACTIVE_ZONE,
        TypeMapMarker.PROVIDER_INACTIVE_CANCELLED_ADDRESS,
        TypeMapMarker.PROVIDER_INACTIVE_CANCELLED_ZONE,
        TypeMapMarker.PROVIDER_INACTIVE_POSSIBLE_ADDRESS,
        TypeMapMarker.PROVIDER_INACTIVE_POSSIBLE_ZONE,
    ];
    patientTypes = ['PATIENT_ACTIVE', 'PATIENT_INACTIVE'];
    PROVIDERS_STATUS_COLORS = PROVIDERS_STATUS_COLORS;
    PROVIDERS_STATUS_BACKGROUND_COLORS = PROVIDERS_STATUS_BACKGROUND_COLORS;
    dotClasses: Record<PROVIDER_STATUS, string> = {
        [PROVIDER_STATUS.ACTIVE]: 'dot-active',
        [PROVIDER_STATUS.INACTIVE]: 'dot-inactive',
        [PROVIDER_STATUS.POSSIBLE]: 'dot-possible',
        [PROVIDER_STATUS.CANCELLED]: 'dot-cancelled',
    };

    styles: google.maps.MapTypeStyle[] = [
        {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
        },
        {
            featureType: 'poi',
            elementType: 'geometry',
            stylers: [{ visibility: 'off' }],
        },
    ];

    options: google.maps.MapOptions = {
        mapId: 'initMap',
        zoom: 8,
        controlSize: 30,
        center: { lat: -34.6037, lng: -58.3816 }, // If !access to user geolocation -->defailt center in Buenos Aires
        zoomControl: true,
        zoomControlOptions: {
            position: google.maps.ControlPosition.LEFT_TOP,
        },
        streetViewControl: true,
        streetViewControlOptions: {
            position: google.maps.ControlPosition.LEFT_TOP,
        },
    };

    cardInfoEnabled: TypeMapMarker[] = [
        TypeMapMarker.PATIENT_ACTIVE,
        TypeMapMarker.PATIENT_INACTIVE,
        TypeMapMarker.PROVIDER_ACTIVE_ADDRESS,
        TypeMapMarker.PROVIDER_ACTIVE_ZONE,
        TypeMapMarker.PROVIDER_INACTIVE_ADDRESS,
        TypeMapMarker.PROVIDER_INACTIVE_ZONE,
        TypeMapMarker.PROVIDER_INACTIVE_CANCELLED_ADDRESS,
        TypeMapMarker.PROVIDER_INACTIVE_CANCELLED_ZONE,
        TypeMapMarker.PROVIDER_INACTIVE_POSSIBLE_ADDRESS,
        TypeMapMarker.PROVIDER_INACTIVE_POSSIBLE_ZONE,
        TypeMapMarker.CLUSTER,
    ]
    infoContent = 'Hola infoContent';
    markerContent: { [key: string]: SVGElement } = {};
    selectedMarker: MarkerGoogleMaps;
    _markers: Subscription;
    _markersMembers: Subscription;
    expandedMemberId: number | null = null;
    cdr: any;
    _dialogRef;
    dspInjector!: Injector;
    providersManagementCmp = ProvidersManagementComponent;


    get localCenter() {
        return this._localCenter
    }
    set localCenter(value: MarkerGoogleMaps) {
        this._localCenter = value
        this.actualCenter.emit(this.localCenter)
    }

    get activeClusterTemplate() {
        return this.clusterProvidersTemplate ;
      }

    constructor(
        public mapsService: MapsService,
        public mapsFacade: MapsFacade,
        public entitiesFacade: EntitiesFacade,
        public alertService: AlertService,
        public generalService: GeneralService,
        private router: Router,
        private authService: AuthenticationService,
        public dialog: MatDialog,
        private injector: Injector,
        @Inject(DOCUMENT) private document: Document,
    ) {
        effect(() => {
            if (!!this.markerSelected()) {
                this.showMarkerInfo(this.markerSelected().marker)
            }
        });
    }

    ngOnInit(): void {

        if (navigator.geolocation && !this.centerFlag) {
            navigator.geolocation.getCurrentPosition(
                position => {
                    const userLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };
                    if (this.map && this.map.googleMap) {
                        this.map.googleMap.setCenter(userLocation);
                        this.animateZoom(13)
                        // this.onCenter = userLocation
                        this.localCenter = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                            type: TypeMapMarker.GENERAL
                        }
                    }
                },
                error => {
                    console.error('Error getting user location', error);
                }
            );
        }

        this._markers = this.mapsFacade.getMarkers$()
            .subscribe((markers) => {
                if (!!markers) {
                    const seenIds = new Set<number>();
                    const uniqueMarkers = markers.filter(m => {
                      if (seenIds.has(m.id)) return false;
                      seenIds.add(m.id);
                      return true;
                    });
                    const markersClean = uniqueMarkers.filter(item => {
                        const isValidCluster = item.clusterSize === undefined || item.clusterSize > 0;
                        if ((item.type === 'GENERAL' || item.type === 'CASE') && isValidCluster) {
                            this.centerFlag = true;
                            item.visible = true;
                            this.localCenter = { lat: item.lat, lng: item.lng, type: item.type };
                            this.updateMapOptions(this.localCenter);
                        }
                        return isValidCluster && item.visible
                    });
                    this.localMarkers = markersClean;
                }
            });

        this._markersMembers = this.mapsFacade.getAllClusterMembers$().subscribe((members) => {
            if (members && members.length > 0) {
                let markersClean = members.filter(item => item.visible)
                this.allClusterMembers = markersClean;
            } else {
                this.allClusterMembers = [];
            }
        })

    }

    private updateMapOptions(value?): void {
        if (value && this.map) {
            const userLocation = {
                lat: value?.lat,
                lng: value?.lng
            };
            this.map.googleMap.setCenter(userLocation);
        }
    }

    animateZoom(targetZoom: number) {
        if (this.map) {
            const currentZoom = this.map.googleMap.getZoom();
            if (currentZoom !== targetZoom) {
                const zoomInterval = setInterval(() => {
                    const newZoom = currentZoom < targetZoom ? this.map!.getZoom()! + 1 : this.map!.getZoom()! - 1;
                    this.map.googleMap.setZoom(newZoom);

                    if (newZoom === targetZoom) {
                        clearInterval(zoomInterval);
                    }
                }, 300); // zoom speed
            }
        }
    }

    copyToClipboard(value) { //copy text from medical license
        navigator.clipboard.writeText(value)
            .then(() => {
                this.alertService.openInfo('Texto copiado al portapapeles');
            })
            .catch(err => {
                console.error('Error al copiar:', err);
            });
    }

    openInfo(markerElem: MapAdvancedMarker, markerData: MarkerGoogleMaps): void { //open poi card
        if (this.cardInfoEnabled.includes(markerData.type)) {
            if (this.selectedMarker === markerData) {
                this.info.close();
                this.selectedMarker = null;
            } else {
                this.selectedMarker = markerData;
                this.info.open(markerElem);
            }
        }
    }

    getInfoWindowTemplate(marker: MarkerGoogleMaps): TemplateRef<any> { //get template for info window
        switch (marker.type) {

            case TypeMapMarker.CASE:
                return this.templateCase;

            case TypeMapMarker.PATIENT_ACTIVE:
            case TypeMapMarker.PATIENT_INACTIVE:
                return this.templatePatient;

            case TypeMapMarker.PROVIDER_ACTIVE_ADDRESS:
            case TypeMapMarker.PROVIDER_ACTIVE_ZONE:
            case TypeMapMarker.PROVIDER_INACTIVE_ADDRESS:
            case TypeMapMarker.PROVIDER_INACTIVE_ZONE:
            case TypeMapMarker.PROVIDER_INACTIVE_CANCELLED_ADDRESS:
            case TypeMapMarker.PROVIDER_INACTIVE_CANCELLED_ZONE:
            case TypeMapMarker.PROVIDER_INACTIVE_POSSIBLE_ADDRESS:
            case TypeMapMarker.PROVIDER_INACTIVE_POSSIBLE_ZONE:
                return this.templateProvider;

            case TypeMapMarker.CLUSTER:
                if (this.clusterMembersVisibilityVerify(marker)) {
                    return this.templateCluster;
                }
                this.info.close();
                return null;

            default:
                return null;
        }
    }

    onInfoClose(): void { //callback on close poi card
        this.selectedMarker = null;
        this.expandedMemberId = null;
        // this.markerSelected = null; // Fixed assignment
    }

    showMarkerInfo(marker: MarkerGoogleMaps): void { //show cluster card
        this.info.close();
        this.selectedMarker = null;
        const selected = this.markerSelected()?.clusterMember;
        if (!!marker.type && marker.type === TypeMapMarker.CLUSTER && this.allClusterMembers.length > 0) {
            this.allClusterMembers.forEach(m => {
                if (m.id === selected && m.visible) {
                    m.selected = true
                    setTimeout(() => {
                        m.selected = false
                    }, 1500);
                }
            })
            this.onClickCluster(marker.id, marker.type);
        } else {
            this.onClickMarker(marker.id)
        }
    }

    onClickCluster(markerId: number, type: TypeMapMarker): void { //show cluster members
        // filter clusters members by id
        this.selectedClusterMembers = this.allClusterMembers.filter(
            m => m.clusterId === markerId
        );

        const markerElem = this.markerElements.toArray().find((elem, idx) => {
            const mData = this.localMarkers[idx];
            return mData.type === type && mData.id === markerId;
        });

        // mark the cluster member as selected
        const clusterData = this.localMarkers.find(
            m => m.type === type && m.id === markerId
        );
        if (markerElem && clusterData) {
            this.selectedMarker = clusterData;
            this.info.open(markerElem);
        } else {
            console.error('No se encontró el cluster con ID:', markerId);
        }
    }

    onClickMarker(markerId: number): void { //find marker from marker list
        if (!this.markerElements || this.markerElements.length === 0) {
            console.error('Marker elements not ready yet.');
            return;
        }
        const markerElem = this.markerElements.toArray().find((elem, index) => {
            const markerData = this.localMarkers[index];
            return markerData && markerData.id === markerId;
        });
        if (markerElem) {
            const markerData = this.localMarkers.find(m => m.id === markerId);
            if (markerData) {
                this.openInfo(markerElem, markerData);
            }
        } else {
            console.error('No se encontró el marcador con el ID:', markerId);
        }
    }

    verifyUserTypeAdmin() {
        return this.authService.isAdmin();
    }

    goToCase(caseId: number): void {
        const isAdmin = this.verifyUserTypeAdmin();

        const route = isAdmin ? ROUTES.PROVISIONS_CONTROL : ROUTES.CASES;
        const title = isAdmin ? 'Control prestacional' : 'Listado de casos';

        this.generalService.setGoBack({ route, title });

        const url = this.router.serializeUrl(
            this.router.createUrlTree([route, caseId])
        );

        window.open(url, '_blank');
    }

    getStateLetter(s: string): string { //return firt letter of poi state
        if (!s) return '';
        const key = s.toUpperCase();

        switch (key) {
            case 'ACTIVE':
                return 'A';
            case 'INACTIVE_POSSIBLE':
                return 'P';
            case 'INACTIVE':
                return 'I';
            case 'INACTIVE_CANCELLED':
                return 'C';
            default:
                return '';
        }
    };


    setMarkerInClusterSelected(marker: MarkerGoogleMaps): void {
        this.markerInClusterSelected = marker;
    }

    deleteMarkerInClusterSelected() { //remove cluster member on cluster card from marker list checkbox
        this.markerInClusterSelected = null;
    }

    toggleMember(m) { // expand poi info on clustrer card
        this.expandedMemberId = this.expandedMemberId === m.id ? null : m.id;

        setTimeout(() => {
            if (this.expandedMemberId === m.id) {
                const element = document.getElementById(`detail-${m.id}`);
                if (element) {
                    // focus to target
                    element.focus();
                    // scroll
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        });
    }

    findPatientsOnCluster(array: MarkerGoogleMaps[]): boolean { //check if there are patients on cluster card
        for (const m of array) {

            if (m.type == TypeMapMarker.PATIENT_ACTIVE || m.type == TypeMapMarker.PATIENT_INACTIVE) {
                return true;
            }
        }
        return false; // Ensure a value is returned if no patients are found
    }

    findProvidersOnCluster(array: MarkerGoogleMaps[]): boolean { //check if there are patients on cluster card
        for (const m of array) {
            if (this.providerTypes.includes(m.type)) {
                return true;
            }
        }
        return false; // Ensure a value is returned if no patients are found
    }


    onToggleProviders(checked: boolean): void {
        this.showingProviders = checked;

        this.selectedClusterMembers.forEach(m => {
            m.visible = checked
                ? this.providerTypes.includes(m.type)
                : this.patientTypes.includes(m.type);
        });
    }

    clusterMembersVisibilityVerify(cluster: MarkerGoogleMaps): boolean { //check if there are visible members on cluster card
        let markers = this.allClusterMembers.filter(m => m.clusterId === cluster.id);
        return markers.some(m => m.visible)
    }

    ngOnDestroy(): void {
        this.localCenter = null;
        this.markersSignal = [];
        this.selectedMarker = null;
        if (this._markers) {
            this._markers.unsubscribe();
        }
        if (this._markersMembers) {
            this._markersMembers.unsubscribe();
        }
    }

    openExternalUrl(m: MarkerGoogleMaps): void {
        const id = m.caseId;
        const url = this.router.serializeUrl(
          this.router.createUrlTree([ROUTES.PROVISIONS_CONTROL, id])
        );
        const fullUrl = `${window.location.origin}${url}`;
        window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }

    goToProviderSchedule(providerMarker:MarkerGoogleMaps): void {
        const providerId = providerMarker?.oldId ?? '';
        const urlTree = this.router.createUrlTree(['/app', 'gestion', 'agenda', providerId]);
        const relativeUrl = this.router.serializeUrl(urlTree);

        const base = this.document.baseURI || this.document.location.origin + '/';
        const absoluteUrl = new URL(relativeUrl, base).toString();

        window.open(absoluteUrl, '_blank', 'noopener');
    }

    showProviderNewTab(providerMarker: MarkerGoogleMaps, patientsFocus?: boolean): void {
        this.providerShowInfo.emit({ marker: providerMarker, patientsFocus }); // Emit an object with marker and patientsFocus
    }

    openReadOnly(marker: MarkerGoogleMaps): void {
        try{
        let providerId = marker.oldId;
        let qps:ProvidersQPS = {
            id:providerId,
            active:marker.active
        }
        this.entitiesFacade.loadProviders(qps)
        this.dspInjector = Injector.create({
          providers: [
            { provide: MAT_DIALOG_DATA, useValue: { mode: 'DSP', providerId } }
          ],
          parent: this.injector
        });

        this._dialogRef = this.dialog.open(DialogComponent, {
          disableClose: true,
          panelClass: 'dm-dsp-dialog',
          minWidth: '80%',
          maxHeight: '90%',
          data: {
            template: this.providerDSPHost,
            title: `${marker.name}`,
          }
        });
      }catch(error){
        console.error('Error opening read-only DSP:', error);
        }
    }

}
